import { create } from 'zustand';
import { fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';

const FBT = FIELDS.bbTrainings;
const FBC = FIELDS.bbClassrooms;
const FBCe = FIELDS.bbCertificates;
const FBR = FIELDS.bbTrainingRecords;
const FBP = FIELDS.bbPositionPlans;
const FBM = FIELDS.bbMessages;

// ── Types ───────────────────────────────────────────────────

export interface Training {
  id: number;
  name: string;
  category: string;
  provider: string;
  description: string;
  requiredForPositions: string;
  recertMonths: number;
  status: string;
  [k: string]: unknown;
}

export interface Classroom {
  id: number;
  trainingId: number;
  className: string;
  instructorName: string;
  instructorId: string;
  classDate: string;
  startTime: string;
  endTime: string;
  location: string;
  capacity: number;
  enrolled: number;
  waitlistCount: number;
  status: string;
  notes: string;
  [k: string]: unknown;
}

export interface Certificate {
  id: number;
  userId: string;
  userName: string;
  userEmail: string;
  trainingId: string;
  trainingName: string;
  completedDate: string;
  expiresDate: string;
  certCode: string;
  status: string;
  [k: string]: unknown;
}

export interface TrainingRecord {
  id: number;
  trainingId: string;
  userId: string;
  userName: string;
  userEmail: string;
  completedDate: string;
  expiresDate: string;
  score: string;
  status: string;
  certCode: string;
  notes: string;
  [k: string]: unknown;
}

export interface PositionPlan {
  id: number;
  positionName: string;
  description: string;
  requiredTrainings: string;
  sortOrder: number;
  [k: string]: unknown;
}

export interface BBMessage {
  id: number;
  recipientEmail: string;
  senderName: string;
  senderEmail: string;
  subject: string;
  body: string;
  channel: string;
  targetMode: string;
  batchId: string;
  readAt: string;
  sentAt: string;
  [k: string]: unknown;
}

// ── Mappers ─────────────────────────────────────────────────

function mapTraining(r: Record<string, unknown>): Training {
  return {
    id: r.record_id as number,
    name: (r[FBT.name] as string) || '',
    category: (r[FBT.category] as string) || '',
    provider: (r[FBT.provider] as string) || '',
    description: (r[FBT.description] as string) || '',
    requiredForPositions: (r[FBT.requiredForPositions] as string) || '',
    recertMonths: Number(r[FBT.recertMonths]) || 0,
    status: (r[FBT.status] as string) || 'active',
  };
}

function mapClassroom(r: Record<string, unknown>): Classroom {
  return {
    id: r.record_id as number,
    trainingId: Number(r[FBC.trainingId]) || 0,
    className: (r[FBC.className] as string) || '',
    instructorName: (r[FBC.instructorName] as string) || '',
    instructorId: (r[FBC.instructorId] as string) || '',
    classDate: (r[FBC.classDate] as string) || '',
    startTime: (r[FBC.startTime] as string) || '',
    endTime: (r[FBC.endTime] as string) || '',
    location: (r[FBC.location] as string) || '',
    capacity: Number(r[FBC.capacity]) || 0,
    enrolled: Number(r[FBC.enrolled]) || 0,
    waitlistCount: Number(r[FBC.waitlistCount]) || 0,
    status: (r[FBC.status] as string) || 'upcoming',
    notes: (r[FBC.notes] as string) || '',
  };
}

function mapCert(r: Record<string, unknown>): Certificate {
  return {
    id: r.record_id as number,
    userId: (r[FBCe.userId] as string) || '',
    userName: (r[FBCe.userName] as string) || '',
    userEmail: (r[FBCe.userEmail] as string) || '',
    trainingId: (r[FBCe.trainingId] as string) || '',
    trainingName: (r[FBCe.trainingName] as string) || '',
    completedDate: (r[FBCe.completedDate] as string) || '',
    expiresDate: (r[FBCe.expiresDate] as string) || '',
    certCode: (r[FBCe.certCode] as string) || '',
    status: (r[FBCe.status] as string) || 'valid',
  };
}

function mapRecord(r: Record<string, unknown>): TrainingRecord {
  return {
    id: r.record_id as number,
    trainingId: (r[FBR.trainingId] as string) || '',
    userId: (r[FBR.userId] as string) || '',
    userName: (r[FBR.userName] as string) || '',
    userEmail: (r[FBR.userEmail] as string) || '',
    completedDate: (r[FBR.completedDate] as string) || '',
    expiresDate: (r[FBR.expiresDate] as string) || '',
    score: (r[FBR.score] as string) || '',
    status: (r[FBR.status] as string) || '',
    certCode: (r[FBR.certCode] as string) || '',
    notes: (r[FBR.notes] as string) || '',
  };
}

function mapPositionPlan(r: Record<string, unknown>): PositionPlan {
  return {
    id: r.record_id as number,
    positionName: (r[FBP.positionName] as string) || '',
    description: (r[FBP.description] as string) || '',
    requiredTrainings: (r[FBP.requiredTrainings] as string) || '',
    sortOrder: Number(r[FBP.sortOrder]) || 0,
  };
}

function mapMessage(r: Record<string, unknown>): BBMessage {
  return {
    id: r.record_id as number,
    recipientEmail: (r[FBM.recipientEmail] as string) || '',
    senderName: (r[FBM.senderName] as string) || '',
    senderEmail: (r[FBM.senderEmail] as string) || '',
    subject: (r[FBM.subject] as string) || '',
    body: (r[FBM.body] as string) || '',
    channel: (r[FBM.channel] as string) || '',
    targetMode: (r[FBM.targetMode] as string) || '',
    batchId: (r[FBM.batchId] as string) || '',
    readAt: (r[FBM.readAt] as string) || '',
    sentAt: (r[FBM.sentAt] as string) || '',
  };
}

// ── Store ───────────────────────────────────────────────────

interface BlackboardState {
  trainings: Training[];
  classrooms: Classroom[];
  certificates: Certificate[];
  trainingRecords: TrainingRecord[];
  positionPlans: PositionPlan[];
  messages: BBMessage[];
  loading: boolean;

  fetchTrainings: () => Promise<void>;
  fetchClassrooms: () => Promise<void>;
  fetchCertificates: () => Promise<void>;
  fetchTrainingRecords: () => Promise<void>;
  fetchPositionPlans: () => Promise<void>;
  fetchMessages: (email?: string) => Promise<void>;

  createTraining: (data: Record<string, unknown>) => Promise<number | null>;
  updateTraining: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteTraining: (id: number) => Promise<void>;

  createClassroom: (data: Record<string, unknown>) => Promise<number | null>;
  updateClassroom: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteClassroom: (id: number) => Promise<void>;

  createCertificate: (data: Record<string, unknown>) => Promise<number | null>;

  createTrainingRecord: (data: Record<string, unknown>) => Promise<number | null>;
  updateTrainingRecord: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteTrainingRecord: (id: number) => Promise<void>;

  createPositionPlan: (data: Record<string, unknown>) => Promise<number | null>;
  updatePositionPlan: (id: number, data: Record<string, unknown>) => Promise<void>;
  deletePositionPlan: (id: number) => Promise<void>;

  sendMessage: (data: Record<string, unknown>) => Promise<number | null>;
}

function buildFields(map: Record<string, string>, data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  for (const [key, fieldKey] of Object.entries(map)) {
    if (data[key] !== undefined) p[fieldKey] = data[key];
  }
  return p;
}

export const useBlackboardStore = create<BlackboardState>((set, get) => ({
  trainings: [],
  classrooms: [],
  certificates: [],
  trainingRecords: [],
  positionPlans: [],
  messages: [],
  loading: false,

  // ── Fetch ────────────────────────────────────────────────

  fetchTrainings: async () => {
    try {
      const r = await fetchRecords(TABLES.bbTrainings, { limit: 200 });
      set({ trainings: r.records.map(mapTraining) });
    } catch (e) { console.error('[BB]', e); }
  },

  fetchClassrooms: async () => {
    try {
      const r = await fetchRecords(TABLES.bbClassrooms, { limit: 200, sort: { field: 'created_at', direction: 'desc' } });
      set({ classrooms: r.records.map(mapClassroom) });
    } catch (e) { console.error('[BB]', e); }
  },

  fetchCertificates: async () => {
    try {
      const r = await fetchRecords(TABLES.bbCertificates, { limit: 500 });
      set({ certificates: r.records.map(mapCert) });
    } catch (e) { console.error('[BB]', e); }
  },

  fetchTrainingRecords: async () => {
    try {
      const r = await fetchRecords(TABLES.bbTrainingRecords, { limit: 500 });
      set({ trainingRecords: r.records.map(mapRecord) });
    } catch (e) { console.error('[BB]', e); }
  },

  fetchPositionPlans: async () => {
    try {
      const r = await fetchRecords(TABLES.bbPositionPlans, { limit: 100 });
      set({ positionPlans: r.records.map(mapPositionPlan) });
    } catch (e) { console.error('[BB]', e); }
  },

  fetchMessages: async (email?: string) => {
    try {
      const r = await fetchRecords(TABLES.bbMessages, { limit: 200 });
      const all = r.records.map(mapMessage);
      set({ messages: email ? all.filter(m => m.recipientEmail === email) : all });
    } catch (e) { console.error('[BB]', e); }
  },

  // ── Training CRUD ────────────────────────────────────────

  createTraining: async (data) => {
    const p = buildFields(FBT, data);
    try {
      const r = await createRecord(TABLES.bbTrainings, p);
      await get().fetchTrainings();
      return (r as any).record_id || (r as any).id || null;
    } catch (e) { console.error('[BB]', e); return null; }
  },
  updateTraining: async (id, data) => {
    const p = buildFields(FBT, data);
    await updateRecord(TABLES.bbTrainings, id, p);
    await get().fetchTrainings();
  },
  deleteTraining: async (id) => {
    await deleteRecord(TABLES.bbTrainings, id);
    await get().fetchTrainings();
  },

  // ── Classroom CRUD ───────────────────────────────────────

  createClassroom: async (data) => {
    const p = buildFields(FBC, data);
    try {
      const r = await createRecord(TABLES.bbClassrooms, p);
      await get().fetchClassrooms();
      return (r as any).record_id || (r as any).id || null;
    } catch (e) { console.error('[BB]', e); return null; }
  },
  updateClassroom: async (id, data) => {
    const p = buildFields(FBC, data);
    await updateRecord(TABLES.bbClassrooms, id, p);
    await get().fetchClassrooms();
  },
  deleteClassroom: async (id) => {
    await deleteRecord(TABLES.bbClassrooms, id);
    await get().fetchClassrooms();
  },

  // ── Certificate CRUD ─────────────────────────────────────

  createCertificate: async (data) => {
    const p = buildFields(FBCe, data);
    try {
      const r = await createRecord(TABLES.bbCertificates, p);
      await get().fetchCertificates();
      return (r as any).record_id || (r as any).id || null;
    } catch (e) { console.error('[BB]', e); return null; }
  },

  // ── Training Record CRUD ─────────────────────────────────

  createTrainingRecord: async (data) => {
    const p = buildFields(FBR, data);
    try {
      const r = await createRecord(TABLES.bbTrainingRecords, p);
      await get().fetchTrainingRecords();
      return (r as any).record_id || (r as any).id || null;
    } catch (e) { console.error('[BB]', e); return null; }
  },
  updateTrainingRecord: async (id, data) => {
    const p = buildFields(FBR, data);
    await updateRecord(TABLES.bbTrainingRecords, id, p);
    await get().fetchTrainingRecords();
  },
  deleteTrainingRecord: async (id) => {
    await deleteRecord(TABLES.bbTrainingRecords, id);
    await get().fetchTrainingRecords();
  },

  // ── Position Plan CRUD ───────────────────────────────────

  createPositionPlan: async (data) => {
    const p = buildFields(FBP, data);
    try {
      const r = await createRecord(TABLES.bbPositionPlans, p);
      await get().fetchPositionPlans();
      return (r as any).record_id || (r as any).id || null;
    } catch (e) { console.error('[BB]', e); return null; }
  },
  updatePositionPlan: async (id, data) => {
    const p = buildFields(FBP, data);
    await updateRecord(TABLES.bbPositionPlans, id, p);
    await get().fetchPositionPlans();
  },
  deletePositionPlan: async (id) => {
    await deleteRecord(TABLES.bbPositionPlans, id);
    await get().fetchPositionPlans();
  },

  // ── Messages ─────────────────────────────────────────────

  sendMessage: async (data) => {
    const p: Record<string, unknown> = { [FBM.sentAt]: new Date().toISOString() };
    if (data.recipientEmail) p[FBM.recipientEmail] = data.recipientEmail;
    if (data.subject) p[FBM.subject] = data.subject;
    if (data.body) p[FBM.body] = data.body;
    if (data.channel) p[FBM.channel] = data.channel;
    if (data.senderName) p[FBM.senderName] = data.senderName;
    if (data.senderEmail) p[FBM.senderEmail] = data.senderEmail;
    if (data.targetMode) p[FBM.targetMode] = data.targetMode;
    if (data.batchId) p[FBM.batchId] = data.batchId;
    try {
      const r = await createRecord(TABLES.bbMessages, p);
      return (r as any).record_id || (r as any).id || null;
    } catch (e) { console.error('[BB]', e); return null; }
  },

  // V1 compat aliases
  users: [] as any[],
  loadUsers: async () => { await (get() as any).fetchUsers?.(); },
  loadTrainings: async () => { await (get() as any).fetchTrainings(); },
  loadTrainingRecords: async () => { await (get() as any).fetchTrainingRecords(); },
  loadClassrooms: async () => { await (get() as any).fetchClassrooms(); },
  loadCertificates: async () => { await (get() as any).fetchCertificates(); },
  loadMessages: async () => { await (get() as any).fetchMessages(); },
  loadPositionPlans: async () => { await (get() as any).fetchPositionPlans(); },
  assignments: [] as any[],
  loadAssignments: async () => {},
  markMessageRead: async (_id: any) => {},
  createAssignment: async (_data: any) => null,
  deleteAssignment: async (_id: any) => {},
  savePositionPlan: async (_id: any, _data: any) => {},
  sendMessages: async (_data: any) => { await (get() as any).sendMessage(_data); },
}));
